import React, { useState } from "react";
import { FaClipboardList, FaExclamationCircle } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import BottomNav from "../../../components/BottomNav";
import HeaderWithBack from "../../../components/HeaderWithBack";
import AccountSwitcher from "../../../components/AccountSwitcher";
// import { useAuth } from "../../../contexts/AuthContext";
import { useAuthStore } from "../../../stores/authStores";

interface Pertanyaan {
  berat: string;
  tinggi: string;
  lamaSakit: string;
  minumObat: string;
  riwayatCovid: string;
}

const Disclaimer: React.FC = () => {
  const navigate = useNavigate();
  const { practitionerRoleId } = useParams<{ practitionerRoleId: string }>();
  const { user: userData } = useAuthStore();
  const isAuto = practitionerRoleId === "auto";

  const [pertanyaan, setPertanyaan] = useState<Pertanyaan>(() => {
    const saved = localStorage.getItem("teleconsult_disclaimer");
    return saved
      ? JSON.parse(saved)
      : {
          berat: "",
          tinggi: "",
          lamaSakit: "",
          minumObat: "",
          riwayatCovid: "",
        };
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPertanyaan((prev) => {
      const updated = { ...prev, [name]: value };
      localStorage.setItem("teleconsult_disclaimer", JSON.stringify(updated));
      return updated;
    });
  };

  const handleContinue = () => {
    if (!pertanyaan.berat || !pertanyaan.tinggi) {
      alert("Mohon isi berat dan tinggi badan");
      return;
    }
    localStorage.setItem("teleconsult_disclaimer", JSON.stringify(pertanyaan));

    navigate(`/checkout-teleconsultation/${practitionerRoleId}`, {
      state: {
        isAuto,
        pertanyaan,
      },
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <HeaderWithBack title="Disclaimer" />

      {/* Profile */}
      <div className="flex justify-center mt-4">
        <div className="bg-white p-4 rounded-xl shadow-md text-center w-11/12 max-w-md">
          <img
            src={
              userData?.person?.avatar_url ||
              `https://m.linksehat.com/assets/img/users/${userData?.person?.gender}-avatar.png`
            }
            alt="Avatar"
            className="w-16 h-16 rounded-full mx-auto mb-2"
          />
          <AccountSwitcher
            className="relative"
            menuClassName="absolute left-1/2 top-full z-50 mt-3 w-full -translate-x-1/2 overflow-hidden rounded-lg border border-gray-200 bg-white text-left shadow-lg"
            renderTrigger={({ currentName, currentLabel, isOpen, isSwitching, toggle }) => (
              <>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-haspopup="listbox"
                  disabled={isSwitching}
                  onClick={toggle}
                  className="inline-flex items-center justify-center gap-1 text-teal-700 font-semibold disabled:cursor-wait"
                >
                  <span>{currentName}</span>
                  <svg
                    aria-hidden="true"
                    className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <p className="text-xs text-teal-600">{currentLabel}</p>
              </>
            )}
          />
        </div>
      </div>

      {/* Pertanyaan */}
      <div className="flex justify-center mt-4">
        <div className="bg-white p-4 rounded-xl shadow-md w-11/12 max-w-md">
          <div className="flex items-center mb-2">
            <FaClipboardList className="text-teal-500 mr-2" />
            <h2 className="text-teal-600 font-bold text-lg">Pertanyaan</h2>
          </div>
          <p className="mb-4">Silahkan jawab Pertanyaan berikut ini:</p>

          {/* Input Berat */}
          <div className="mb-4">
            <label className="font-medium">
              Berapa berat badan Anda? <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              placeholder="Masukkan berat badan (kg)"
              className="w-full mt-1 p-2 border rounded"
              name="berat"
              value={pertanyaan.berat}
              onChange={handleInputChange}
            />
          </div>

          {/* Input Tinggi */}
          <div className="mb-4">
            <label className="font-medium">
              Berapa tinggi badan Anda? <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              placeholder="Masukkan tinggi badan (cm)"
              className="w-full mt-1 p-2 border rounded"
              name="tinggi"
              value={pertanyaan.tinggi}
              onChange={handleInputChange}
            />
          </div>

          {/* Radio: Sakit lebih 3 hari */}
          <div className="mb-4">
            <label className="font-medium">
              Apakah sakit sudah lebih dari 3 hari?
            </label>
            <div className="flex gap-4 mt-1">
              <label>
                <input
                  type="radio"
                  name="lamaSakit"
                  onChange={handleInputChange}
                  value="1"
                  checked={pertanyaan.lamaSakit === "1"}
                />{" "}
                Ya
              </label>
              <label>
                <input
                  type="radio"
                  name="lamaSakit"
                  onChange={handleInputChange}
                  value="0"
                  checked={pertanyaan.lamaSakit === "0"}
                />{" "}
                Tidak
              </label>
            </div>
          </div>

          {/* Radio: Sudah minum obat */}
          <div className="mb-4">
            <label className="font-medium">Apakah sudah minum obat?</label>
            <div className="flex gap-4 mt-1">
              <label>
                <input
                  type="radio"
                  name="minumObat"
                  onChange={handleInputChange}
                  value="1"
                  checked={pertanyaan.minumObat === "1"}
                />{" "}
                Ya
              </label>
              <label>
                <input
                  type="radio"
                  name="minumObat"
                  onChange={handleInputChange}
                  value="0"
                  checked={pertanyaan.minumObat === "0"}
                />{" "}
                Tidak
              </label>
            </div>
          </div>

          {/* Radio: Riwayat Covid */}
          <div className="mb-4">
            <label className="font-medium">
              Apakah anda memiliki riwayat covid?
            </label>
            <div className="flex gap-4 mt-1">
              <label>
                <input
                  type="radio"
                  name="riwayatCovid"
                  onChange={handleInputChange}
                  value="1"
                  checked={pertanyaan.riwayatCovid === "1"}
                />{" "}
                Ya
              </label>
              <label>
                <input
                  type="radio"
                  name="riwayatCovid"
                  onChange={handleInputChange}
                  value="0"
                  checked={pertanyaan.riwayatCovid === "0"}
                />{" "}
                Tidak
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer Box */}
      <div className="flex justify-center mt-4 mb-4 px-4">
        <div className="w-full md:w-7/12 bg-white text-gray-800 rounded-lg shadow-lg border border-gray-200">
          <div className="p-3">
            <h3 className="text-2xl font-semibold flex items-center gap-2">
              <FaExclamationCircle className="text-teal-500" />
              Disclaimer
            </h3>
          </div>
          <hr className="w-full border-gray-200" />
          <div className="p-3 text-sm">
            <p>
              Fasilitas ini belum menjamin kepastian diagnosa kondisi yang
              dialami. Perbincangan dalam aplikasi tidak bisa menggantikan
              interaksi fisik dengan dokter. Untuk kondisi darurat, segera temui
              dokter langsung.
            </p>
          </div>
        </div>
      </div>

      {/* Tombol Cancel dan Continue */}
      <div className="flex w-full justify-between mt-4 mb-28 px-4">
        <button
          onClick={() => navigate("/home")}
          className="px-6 py-2 text-gray-800 uppercase bg-white rounded-full shadow-lg"
        >
          Cancel
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-2 text-white uppercase rounded-full shadow-lg bg-teal-500"
        >
          Continue
        </button>
      </div>

      <BottomNav />
    </div>
  );
};

export default Disclaimer;
